import { useCallback, useEffect, useRef, useState } from "react";
import { Audio } from "expo-av";
import { Platform } from "react-native";
import { useTranslation } from "react-i18next";

import Sentry from "../utils/sentry";
import { getValidAccessToken, refreshAccessToken } from "../utils/authRefresh";

export type ChatSpeechInputPhase = "idle" | "recording" | "transcribing";

export interface ChatSpeechInputError {
  title: string;
  message: string;
  onRetry?: () => void;
  retryButtonText?: string;
}

interface UseChatSpeechInputParams {
  isDisabled: boolean;
  onTranscriptReady: (transcript: string) => void;
  onError?: (payload: ChatSpeechInputError) => void;
}

interface RecordingRef {
  recording: Audio.Recording;
  fileName: string;
  mimeType: string;
}

const AUDIO_LEVEL_FRAME_MS = 60;

export const appendTranscriptToDraft = (draft: string, transcript: string) => {
  const trimmedTranscript = transcript.trim();

  if (!trimmedTranscript) {
    return draft;
  }

  if (!draft.trim()) {
    return trimmedTranscript;
  }

  const separator = /[\s\n]$/.test(draft) ? "" : " ";
  return `${draft}${separator}${trimmedTranscript}`;
};

const getMicrophoneErrorMessage = (error: unknown) => {
  const errorName = error instanceof Error ? error.name : "";
  const loweredMessage = error instanceof Error ? error.message.toLowerCase() : "";

  if (
    errorName === "NotAllowedError" ||
    errorName === "PermissionDeniedError" ||
    loweredMessage.includes("permission")
  ) {
    return "permission";
  }

  if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError" || loweredMessage.includes("device")) {
    return "unavailable";
  }

  return "generic";
};

const getWebRecordingConfig = () => {
  if (typeof navigator !== "undefined" && /^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
    return { fileName: "recording.mp4", mimeType: "video/mp4" };
  }

  return { fileName: "recording.webm", mimeType: "audio/webm" };
};

function useChatSpeechInput({ isDisabled, onTranscriptReady, onError }: UseChatSpeechInputParams) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<ChatSpeechInputPhase>("idle");
  const [audioLevel, setAudioLevel] = useState(0);
  const recordingRef = useRef<RecordingRef | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const analyserDataRef = useRef<Uint8Array | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastFrameAtRef = useRef(0);

  const stopAudioLevelMonitoring = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (audioContextRef.current) {
      void audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    analyserDataRef.current = null;
    setAudioLevel(0);
  }, []);

  const stopMediaTracks = useCallback(() => {
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  }, []);

  const cleanupWebResources = useCallback(() => {
    stopAudioLevelMonitoring();
    stopMediaTracks();
  }, [stopAudioLevelMonitoring, stopMediaTracks]);

  useEffect(() => {
    return () => {
      if (recordingRef.current) {
        void recordingRef.current.recording.stopAndUnloadAsync().catch(() => undefined);
      }

      cleanupWebResources();
    };
  }, [cleanupWebResources]);

  const emitError = useCallback(
    (title: string, message: string, onRetry?: () => void) => {
      onError?.({
        title,
        message,
        onRetry,
        retryButtonText: t("Retry"),
      });
    },
    [onError, t]
  );

  const startAudioLevelMonitoring = useCallback((stream: MediaStream) => {
    if (Platform.OS !== "web") {
      return;
    }

    try {
      const AudioContextConstructor =
        window.AudioContext ||
        (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

      if (!AudioContextConstructor) {
        return;
      }

      const audioContext = new AudioContextConstructor();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.85;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      analyserDataRef.current = new Uint8Array(analyser.fftSize);
      lastFrameAtRef.current = 0;

      const updateAudioLevel = (timestamp: number) => {
        if (!analyserRef.current || !analyserDataRef.current) {
          return;
        }

        if (!lastFrameAtRef.current || timestamp - lastFrameAtRef.current >= AUDIO_LEVEL_FRAME_MS) {
          analyserRef.current.getByteTimeDomainData(analyserDataRef.current as Uint8Array);

          let sumSquares = 0;
          for (let i = 0; i < analyserDataRef.current.length; i += 1) {
            const normalizedSample = (analyserDataRef.current[i] - 128) / 128;
            sumSquares += normalizedSample * normalizedSample;
          }

          const rms = Math.sqrt(sumSquares / analyserDataRef.current.length);
          setAudioLevel(Math.min(1, rms * 3.2));
          lastFrameAtRef.current = timestamp;
        }

        animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
      };

      animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
    } catch (error) {
      Sentry.captureException(error);
    }
  }, []);

  const transcribeAudioToText = useCallback(
    async (audioSource: Blob | { uri: string; name: string; type: string }): Promise<string> => {
      const token = await getValidAccessToken({ minTtlSeconds: 30 });

      if (!token) {
        throw new Error("missing_access_token");
      }

      const buildFormData = () => {
        const formData = new FormData();

        if (Platform.OS === "web") {
          const webAudioSource = audioSource as Blob & { name?: string };
          formData.append("audio", webAudioSource, webAudioSource.name || "recording.webm");
        } else {
          formData.append("audio", audioSource as unknown as Blob);
        }

        return formData;
      };

      let response = await fetch("/api/audio/transcribe", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: buildFormData(),
      });

      if (response.status === 401 || response.status === 403) {
        const refreshed = await refreshAccessToken();

        if (refreshed?.accessToken) {
          response = await fetch("/api/audio/transcribe", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${refreshed.accessToken}`,
            },
            body: buildFormData(),
          });
        }
      }

      if (!response.ok) {
        throw new Error(`transcription_failed_${response.status}`);
      }

      const data = await response.json();
      return (data?.text || "").trim();
    },
    []
  );

  const startRecording = useCallback(async () => {
    if (isDisabled || phase !== "idle") {
      return;
    }

    try {
      if (Platform.OS === "web" && typeof navigator !== "undefined" && navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaStreamRef.current = stream;
        startAudioLevelMonitoring(stream);
      }

      const permissionResponse = await Audio.requestPermissionsAsync();
      if (permissionResponse.status !== "granted") {
        throw new Error("microphone_permission_denied");
      }

      await Audio.setAudioModeAsync({
        allowsRecordingIOS: true,
        playsInSilentModeIOS: true,
      });

      const webRecordingConfig = getWebRecordingConfig();
      const recordingOptions =
        Platform.OS === "web"
          ? {
              ...Audio.RecordingOptionsPresets.HIGH_QUALITY,
              web: {
                mimeType: webRecordingConfig.mimeType,
                bitsPerSecond: 128000,
              },
            }
          : Audio.RecordingOptionsPresets.HIGH_QUALITY;

      const { recording } = await Audio.Recording.createAsync(recordingOptions);

      recordingRef.current = {
        recording,
        fileName: Platform.OS === "web" ? webRecordingConfig.fileName : "recording.m4a",
        mimeType: Platform.OS === "web" ? webRecordingConfig.mimeType : "audio/m4a",
      };

      setPhase("recording");
    } catch (error) {
      cleanupWebResources();
      recordingRef.current = null;
      setPhase("idle");
      Sentry.captureException(error);

      const microphoneErrorType = getMicrophoneErrorMessage(error);
      if (
        microphoneErrorType === "permission" ||
        microphoneErrorType === "unavailable" ||
        microphoneErrorType === "generic"
      ) {
        emitError(
          t("Microphone unavailable"),
          t("We couldn't access your microphone. Check your browser or device permissions and try again."),
          () => {
            void startRecording();
          }
        );
      }
    }
  }, [cleanupWebResources, emitError, isDisabled, phase, startAudioLevelMonitoring, t]);

  const stopExpoRecording = useCallback(async () => {
    const activeRecording = recordingRef.current;
    if (!activeRecording) {
      return null;
    }

    await activeRecording.recording.stopAndUnloadAsync();
    const uri = activeRecording.recording.getURI();
    recordingRef.current = null;

    if (!uri) {
      throw new Error("missing_recording_uri");
    }

    if (Platform.OS === "web") {
      const response = await fetch(uri);
      const audioBlob = await response.blob();

      if (audioBlob.size === 0) {
        throw new Error("empty_audio_blob");
      }

      return new File([audioBlob], activeRecording.fileName, {
        type: audioBlob.type || activeRecording.mimeType,
      });
    }

    return {
      uri,
      name: activeRecording.fileName,
      type: activeRecording.mimeType,
    };
  }, []);

  const resetAfterRecording = useCallback(async () => {
    cleanupWebResources();
    recordingRef.current = null;

    try {
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: false,
        playsInSilentModeIOS: true,
      });
    } catch (error) {
      Sentry.captureException(error);
    }

    setPhase("idle");
  }, [cleanupWebResources]);

  const stopRecording = useCallback(async () => {
    if (phase !== "recording" || !recordingRef.current) {
      return;
    }

    setPhase("transcribing");

    try {
      const audioSource = await stopExpoRecording();

      if (!audioSource) {
        throw new Error("missing_audio_source");
      }

      const transcript = await transcribeAudioToText(audioSource);

      if (!transcript) {
        emitError(
          t("No speech detected"),
          t("We couldn't detect any speech in your recording. Please try again."),
          () => {
            void startRecording();
          }
        );
        return;
      }

      onTranscriptReady(transcript);
    } catch (error) {
      Sentry.captureException(error);

      emitError(t("Transcription failed"), t("We couldn't transcribe your recording. Please try again."), () => {
        void startRecording();
      });
    } finally {
      await resetAfterRecording();
    }
  }, [
    emitError,
    onTranscriptReady,
    phase,
    resetAfterRecording,
    startRecording,
    stopExpoRecording,
    t,
    transcribeAudioToText,
  ]);

  const cancelRecording = useCallback(async () => {
    if (phase !== "recording" || !recordingRef.current) {
      return;
    }

    try {
      await recordingRef.current.recording.stopAndUnloadAsync();
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      await resetAfterRecording();
    }
  }, [phase, resetAfterRecording]);

  const handleRecordingStartPress = useCallback(() => {
    void startRecording();
  }, [startRecording]);

  const handleRecordingStopPress = useCallback(() => {
    void stopRecording();
  }, [stopRecording]);

  const handleRecordingCancelPress = useCallback(() => {
    void cancelRecording();
  }, [cancelRecording]);

  return {
    audioLevel,
    phase,
    handleRecordingCancelPress,
    handleRecordingStartPress,
    handleRecordingStopPress,
  };
}

export default useChatSpeechInput;
